import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Award, Download, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { openCertificatePrintWindow } from "@/lib/certificateTemplate";
import certificateBackground from "@/assets/certificate-background.jpg";
import eabLogoHorizontal from "@/assets/eab-logo-horizontal.png";

const sampleCertificate = {
  user_name: "Jane Entrepreneur",
  curriculum_title: "Entrepreneurship Fundamentals",
  certificate_number: "CERT-20251223-DEMO1234",
  issued_at: new Date().toISOString(),
  instructor_name: "Dr. Sarah Mitchell",
};

export default function CertificateDemo() {
  const navigate = useNavigate();
  const issueDate = format(new Date(sampleCertificate.issued_at), "MMMM d, yyyy");

  const handleDownload = () => {
    openCertificatePrintWindow({
      userName: sampleCertificate.user_name,
      courseName: sampleCertificate.curriculum_title,
      certificateNumber: sampleCertificate.certificate_number,
      issuedAt: sampleCertificate.issued_at,
      instructorName: sampleCertificate.instructor_name,
    });
  };

  return (
    <div className="min-h-screen bg-background p-8">
      {/* Google Fonts for Tangerine */}
      <link href="https://fonts.googleapis.com/css2?family=Tangerine:wght@700&display=swap" rel="stylesheet" />
      
      <div className="max-w-5xl mx-auto space-y-8">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <h1 className="text-3xl font-bold text-foreground">Certificate Preview Demo</h1>
        
        {/* Certificate Preview */}
        <Card className="overflow-hidden">
          <CardHeader className="bg-muted/50">
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-primary" />
              Sample Certificate
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="bg-muted/20 p-8">
              <div 
                className="mx-auto relative overflow-hidden"
                style={{ 
                  width: "100%",
                  maxWidth: "1100px",
                  aspectRatio: "1100/780",
                  backgroundImage: `url(${certificateBackground})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                }}
              >
                {/* Inner White Box with lined background - centered content */}
                <div 
                  className="absolute flex flex-col items-center text-center"
                  style={{
                    top: "3.85%",
                    left: "3.64%",
                    right: "3.64%",
                    bottom: "3.85%",
                    background: "rgba(255, 255, 255, 0.9)",
                    backgroundImage: `repeating-linear-gradient(
                      0deg,
                      transparent,
                      transparent 6px,
                      rgba(180, 180, 180, 0.08) 6px,
                      rgba(180, 180, 180, 0.08) 7px
                    )`,
                    padding: "2% 3.64% 2.56%",
                    justifyContent: "flex-start",
                    paddingTop: "3%",
                  }}
                >
                  {/* Logo Section - 67.5% of page width */}
                  <div style={{ marginBottom: "10%", width: "67.5%" }}>
                    <img 
                      src={eabLogoHorizontal} 
                      alt="Entrepreneurs Across Borders" 
                      style={{ width: "100%", height: "auto" }}
                    />
                  </div>
                  
                  {/* Certificate Title */}
                  <h1 
                    className="text-2xl md:text-4xl lg:text-5xl font-normal uppercase"
                    style={{ 
                      color: "#2ea7d9",
                      letterSpacing: "0.25em",
                      marginBottom: "0.5%",
                    }}
                  >
                    Certificate
                  </h1>
                  
                  {/* Of Completion */}
                  <div 
                    className="flex items-center justify-center gap-2 md:gap-3 text-[10px] md:text-xs uppercase"
                    style={{
                      color: "#666",
                      letterSpacing: "2px",
                      marginTop: "0",
                      marginBottom: "3%",
                    }}
                  >
                    <span 
                      className="w-8 md:w-12 h-0.5"
                      style={{ background: "linear-gradient(90deg, #2ea7d9, #3eb891)" }}
                    />
                    of Completion
                    <span 
                      className="w-8 md:w-12 h-0.5"
                      style={{ background: "linear-gradient(90deg, #3eb891, #2ea7d9)" }}
                    />
                  </div>
                  
                  {/* Presented Text */}
                  <p 
                    className="text-[10px] md:text-xs uppercase"
                    style={{
                      color: "#555",
                      letterSpacing: "4px",
                      marginBottom: "2%",
                    }}
                  >
                    We Proudly Present This Certificate To
                  </p>
                  
                  {/* Recipient Name - Tangerine cursive font */}
                  <h2 
                    className="text-4xl md:text-6xl lg:text-7xl font-bold"
                    style={{ 
                      fontFamily: "'Tangerine', cursive",
                      color: "#2ea7d9",
                      marginBottom: "1.5%",
                      lineHeight: 1,
                    }}
                  >
                    {sampleCertificate.user_name}
                  </h2>
                  
                  {/* Course Text */}
                  <p 
                    className="text-xs md:text-sm"
                    style={{ color: "#555", marginBottom: "0" }}
                  >
                    for completing the course{" "}
                    <span style={{ fontWeight: 600, color: "#333" }}>
                      {sampleCertificate.curriculum_title}
                    </span>
                  </p>
                  
                  {/* Instructor Section - centered */}
                  <div className="text-center" style={{ marginTop: "3%" }}>
                    <div 
                      className="font-bold text-xs md:text-sm inline-block"
                      style={{
                        color: "#333",
                        borderTop: "2px solid #2ea7d9",
                        paddingTop: "6px",
                        minWidth: "120px",
                      }}
                    >
                      {sampleCertificate.instructor_name}
                    </div>
                    <div 
                      className="text-[8px] md:text-[10px] uppercase"
                      style={{
                        color: "#666",
                        letterSpacing: "1px",
                        marginTop: "3px",
                      }}
                    >
                      Course Speaker
                    </div>
                  </div>
                  
                  {/* Details Section - centered */}
                  <div className="text-center space-y-0.5 md:space-y-1" style={{ marginTop: "2%" }}>
                    <div 
                      className="text-[9px] md:text-[11px] uppercase"
                      style={{ color: "#555", letterSpacing: "0.5px" }}
                    >
                      Issuing Date:{" "}
                      <span style={{ fontWeight: 600 }}>{issueDate}</span>
                    </div>
                    <div 
                      className="text-[9px] md:text-[11px] uppercase"
                      style={{ color: "#555", letterSpacing: "0.5px" }}
                    >
                      Certificate ID:{" "}
                      <span style={{ fontWeight: 600 }}>{sampleCertificate.certificate_number}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button onClick={handleDownload} size="lg">
            <Download className="h-4 w-4 mr-2" />
            Download / Print Certificate
          </Button>
        </div>
      </div>
    </div>
  );
}
